import { UserType } from "../models/enums/userType.enum";
import { ActivitiyHistory } from "../models/mailHistory";
import {
  ForgotPasswordRequestData,
  MoodleBaseData,
  ProfileUpdateRequestData,
  ResetPasswordRequestData,
  UserData,
  UserRegistrationRequestData,
  UserAccountActivationData,
  ResendActivationCodeData,
  ChangePasswordRequestData,
  ReactivationAccountCodeData,
  NewEventParticipantData,
  ChangePasswordBaseData,
} from "../models/userData";
import apiRoutes from "./apiRoutes";
import { AxiosResponse } from "axios";
import { BaseCrudService } from "./baseCrudService";
import { AuthenticationStatus } from "./authStatus";
import { ApiResponse } from "./apiResponse";
import { Update2FaSettingsResponseBody } from "../models/auth2FaData";

export class UsersService extends BaseCrudService {
  /**
   * Checks the current session state for the user.
   *
   * @returns A promise that resolves to the current authentication status.
   */
  async checkCurrentSession() {
    const response = await this.getAll<AuthenticationStatus>(apiRoutes.session);
    return response;
  }

  /**
   * Retrieves the currently logged-in user's data.
   *
   * @returns A promise that resolves to the current user's data.
   */
  async getCurrentUser() {
    const response = await this.getAll<UserData>(apiRoutes.user);
    return response.data;
  }

  /**
   * Retrieves a list of users filtered by search string, user type, and event ID.
   *
   * @param search - Optional search string to filter users.
   * @param user_type - Optional user type to filter by.
   * @param eventId - Event ID to filter users by event participation.
   * @returns A promise that resolves to a list of filtered users.
   */
  async getUsers(search: string | null, user_type: UserType | null, eventId: string) {
    let data = {};
    if (search) data = { search: search };
    if (user_type) data = { ...data, user_type: user_type, event: eventId };

    const response = await this.getAll<UserData[]>(apiRoutes.users, data);
    return response.data;
  }

  /**
   * Enables or disables two-factor authentication for the current user.
   *
   * @param password - The user's current password.
   * @param is_2fa_enabled - Whether 2FA should be enabled or disabled.
   * @returns A promise resolving to the full Axios response.
   */
  async update2FaSettings(
    password: string,
    is_2fa_enabled: boolean
  ): Promise<AxiosResponse<Update2FaSettingsResponseBody>> {
    return this.patch(apiRoutes.auth.toggle_2fa, {
      password,
      is_2fa_enabled,
    });
  }

  /**
   * Retrieves user data for a given user ID.
   *
   * @param id - The ID of the user.
   * @returns A promise resolving to the user's data.
   */
  async getUser(id: number) {
    const response = await this.get<UserData>(apiRoutes.users, id.toString());
    return response.data;
  }

  /**
   * Retrieves a list of activity statistics filtered by a query string.
   *
   * @param filter - Filter query string to retrieve specific statistics.
   * @returns A promise resolving to a list of activity statistics.
   */
  async getStatistics(filter: string) {
    const response = await this.getAll<ActivitiyHistory[]>(
      apiRoutes.statistics + filter
    );
    return response.data;
  }

  /**
   * Retrieves a single activity statistic by ID.
   *
   * @param id - The ID of the activity statistic.
   * @returns A promise resolving to the activity history record.
   */
  async getStatistic(id: string) {
    const response = await this.get<ActivitiyHistory>(apiRoutes.statistics, id);
    return response.data;
  }

  /**
   * Fetches Moodle-related data for a given event ID.
   *
   * @param id - Event ID.
   * @returns A promise resolving to Moodle base data.
   * @deprecated This method may be removed in future versions.
   */
  async fetchMoodleData(id: number) {
    const response = await this.get<MoodleBaseData>(
      `${apiRoutes.events}`,
      `${id}${apiRoutes.moodle_data}`
    );
    return response.data;
  }

  /**
   * Logs the user into Moodle for the given event ID.
   *
   * @param id - Event ID.
   * @returns A promise resolving to the login response.
   * @deprecated This method may be removed in future versions.
   */
  async loginUserInMoodle(id: number) {
    const response = await this.get(
      `${apiRoutes.events}`,
      `${id}${apiRoutes.moodle_login}`
    );
    return response;
  }

  /**
   * Updates the profile data of the current user.
   *
   * @param data - The updated profile data.
   * @returns Axios promise resolving to the response.
   */
  updateProfile(data: ProfileUpdateRequestData) {
    return this.axiosInstance.patch(apiRoutes.profile, data);
  }

  /**
   * Register user
   *
   * @returns boolean true on success, otherwise false
   */
  async register(data: UserRegistrationRequestData) {
    try {
      await this.create<UserRegistrationRequestData>(apiRoutes.registration, data);

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Activate user account
   *
   * @returns boolean true on success, otherwise false
   */
  async activate(data: UserAccountActivationData) {
    try {
      await this.create<UserAccountActivationData>(apiRoutes.activate_account, data);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Send mail with reactivation link
   *
   * @returns boolean true on success, otherwise false
   */
  async reactivate(data: ReactivationAccountCodeData) {
    try {
      await this.create<ReactivationAccountCodeData>(apiRoutes.reactivate_account, data);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Send mail with activation link
   *
   * @returns boolean true on success, otherwise false
   */
  async resend_activation_code(data: ResendActivationCodeData) {
    try {
      await this.create<ResendActivationCodeData>(
        apiRoutes.resend_activation_code,
        data
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Deactivate the current user's account.
   *
   * @returns boolean true on success, otherwise false
   */
  async deactivateAccount() {
    try {
      await this.getAll<ApiResponse>(apiRoutes.deactivate_account);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Request a password reset link via email.
   *
   * @returns boolean true on success, otherwise false
   */
  async forgotPassword(data: ForgotPasswordRequestData) {
    try {
      await this.create<ForgotPasswordRequestData>(apiRoutes.forgot_password, data);

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Reset the user password using a token.
   *
   * @returns boolean true on success, otherwise false
   */
  async resetPassword(data: ResetPasswordRequestData) {
    try {
      const response = await this.create<ResetPasswordRequestData>(
        apiRoutes.reset_password,
        data
      );
      return response?.status === 200;
    } catch (e) {
      return false;
    }
  }

  /**
   * Change the password of the current user.
   *
   * @returns object containing `ok` boolean to indicate success
   */
  async changePassword(data: ChangePasswordRequestData) {
    try {
      const response = await this.create(apiRoutes.change_password, data);
      return {
        ok: response?.status === 200,
      };
    } catch (e) {
      return {
        ok: false,
      };
    }
  }

  async changeTemporaryPassword(userId: string, data: ChangePasswordBaseData) {
    try {
      return await this.create(
        apiRoutes.users + "/" + userId + apiRoutes.change_temporary_password,
        data
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * Register a new event participant
   *
   * @returns boolean true on success, otherwise false
   */
  async registerEventParticipant(data: NewEventParticipantData) {
    try {
      return await this.create<NewEventParticipantData>(
        apiRoutes.register_event_participant,
        data
      );
    } catch (e) {
      return false;
    }
  }
}
